// ABOUTME: Global route watcher that clears all task localStorage sessions on SPA navigation.
// ABOUTME: Preserves sessions on page reload using a sessionStorage sentinel flag.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { clearAllTaskSessions } from '@/hooks/useTaskSession';

const SESSION_SENTINEL = 'task_session_sentinel';

/**
 * Detects whether the current page load is a continuation of an existing session
 * (i.e. a reload or iframe re-render) vs a truly fresh visit (new tab / closed tab).
 * Uses sessionStorage which survives reloads but is cleared when the tab closes.
 * This is more reliable than the Performance Navigation API, especially inside iframes.
 */
const isExistingSession: boolean = (() => {
  try {
    return sessionStorage.getItem(SESSION_SENTINEL) === '1';
  } catch {
    return false;
  }
})();

// Mark that a session is active (survives reloads, cleared on tab close)
try { sessionStorage.setItem(SESSION_SENTINEL, '1'); } catch {}

// On truly fresh visit (no sentinel), clear all leftover task sessions
if (!isExistingSession) {
  clearAllTaskSessions();
}

/**
 * Clears all task session and task nav localStorage keys whenever
 * the route (pathname + search) changes within the SPA.
 * The initial mount is handled by the module-level clear above.
 */
export function TaskSessionWatcher() {
  const { pathname, search } = useLocation();
  const prevRef = useRef(pathname + search);

  useEffect(() => {
    const current = pathname + search;
    if (current !== prevRef.current) {
      clearAllTaskSessions();
      prevRef.current = current;
    }
  }, [pathname, search]);

  return null;
}
